import { Box, makeStyles, Theme, useMediaQuery } from "@material-ui/core";
import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";

import { WeekDay } from "../../../types/generic.types";
import { SetRangeType } from "./types";

const useStyles = makeStyles((theme) => ({
  slotContainer: {
    display: "flex",
    flexWrap: "wrap",
    margin: -theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  slot: {
    flex: "0 0 50px",
    textAlign: "center",
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    borderRadius: 5,
    background: theme.palette.grey[400],
    border: `1px solid ${theme.palette.grey[500]}`,
    color: theme.palette.grey[50],
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.palette.grey[500],
    },

    "&.selected": {
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.dark}`,
    },

    "&.semi-selected": {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
}));

const timeConverter = (seconds: number) => {
  const minSlot = seconds / 60;
  const hours = Math.floor(minSlot / 60);
  const minutes = minSlot % 60;
  return `${("0" + hours).slice(-2)}:${("0" + minutes).slice(-2)}`;
};

/**
 * @param minSlot {number} this is a number in seconds
 * */
const getSlotsData = (minSlot: number): { time: string; value: number }[] => {
  const slotsNumber = Math.floor((24 * 60 * 60) / minSlot);
  return (
    Array(slotsNumber)
      .fill(0)
      // @ts-ignore
      .map((_, i) => {
        return { time: timeConverter(i * minSlot), value: i * minSlot };
      })
  );
};

const AvailabilitySlots: FC<AvailabilitySlotsProps> = ({
  initialDaySlots,
  minSlot,
  getSlots,
  currentDayName,
  currentDaySlots,
  type = SetRangeType.creation,
}) => {
  const classes = useStyles({});
  const [selectedSlots, setSelectedSlots] = useState(new Set<number>(initialDaySlots));
  const memoizedSlotData = useMemo(() => getSlotsData(minSlot), [minSlot]);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleToggleSlotSelection = (slot: number) => {
    selectedSlots.has(slot) ? selectedSlots.delete(slot) : selectedSlots.add(slot);
    setSelectedSlots(new Set(selectedSlots));
    getSlots(selectedSlots);
  };

  const resetSlots = () => {
    setSelectedSlots(new Set());
    getSlots(new Set());
  };

  useEffect(() => {
    setSelectedSlots(new Set(currentDaySlots || initialDaySlots));
  }, [currentDayName, initialDaySlots, currentDaySlots]);

  useEffect(() => {
    // reset current selected slots on switching mode
    // only if type is edit
    if (type === SetRangeType.edit) resetSlots();

    return () => {
      if (type === SetRangeType.edit) resetSlots();
    };
  }, [currentDayName]);

  useEffect(() => {
    resetSlots();
  }, [minSlot]);

  return (
    <Box className={classes.slotContainer} justifyContent={isSm ? "space-between" : ""}>
      {memoizedSlotData.map((el) => (
        <Box
          key={`${el.value}`}
          className={clsx([
            classes.slot,
            {
              selected: selectedSlots.has(el.value),
            },
          ])}
          onClick={() => handleToggleSlotSelection(el.value)}
        >
          {el.time}
        </Box>
      ))}
    </Box>
  );
};

interface AvailabilitySlotsProps {
  initialDaySlots: Set<number> | undefined;
  minSlot: number;
  getSlots: (slots: Set<number>) => void;
  currentDayName?: WeekDay;
  currentDaySlots?: Set<number>;
  type?: SetRangeType;
}

export default AvailabilitySlots;
